import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  Checkbox,
  FormGroup,
  FormLabel,
  Divider,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Paper,
  ListSubheader,
  InputAdornment,
  FormHelperText
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { api } from '../../../services/authService';
import useLocalStorage from '../../../hooks/useLocalStorage';

const CreateLeadList = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const currentUser = useSelector((state) => state.auth.user);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    list_type: 'manual',
    status: 'draft',
    account_id: '',
    campaign_id: campaignId || '',
    funnel_id: '',
    is_public: false,
    max_leads: '',
    filter_criteria: []
  });
  
  // UI state
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [leadFields, setLeadFields] = useState([]);
  
  // Filter builder state (matching LeadSelectionInterface)
  const [fieldOptions, setFieldOptions] = useState([]);
  const [filters, setFilters] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedField, setSelectedField] = useState('');
  const [selectedOperator, setSelectedOperator] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [fieldSearchQuery, setFieldSearchQuery] = useState('');
  const [debouncedFieldSearchQuery, setDebouncedFieldSearchQuery] = useState('');
  const [combineWith, setCombineWith] = useState('AND');
  const [showRelativeDateHelp, setShowRelativeDateHelp] = useState(false);
  const [dateInputMode, setDateInputMode] = useState('absolute'); // 'absolute' or 'relative'
  const [valueError, setValueError] = useState('');
  
  // Ref for field search input to maintain focus
  const fieldSearchInputRef = useRef(null);
  
  // Local storage
  const [selectedAccount, setSelectedAccount] = useLocalStorage('selectedAccount', '');

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (formData.account_id) {
      fetchCampaigns(formData.account_id);
    } else {
      setCampaigns([]); // Clear campaigns when no account is selected
    }
    // Clear campaign selection when account changes
    setFormData(prev => ({ ...prev, campaign_id: '', funnel_id: '' }));
  }, [formData.account_id]);

  useEffect(() => {
    if (formData.campaign_id) {
      fetchFunnels(formData.campaign_id);
    } else {
      setFunnels([]); // Clear funnels when no campaign is selected
    }
    // Clear funnel selection when campaign changes
    setFormData(prev => ({ ...prev, funnel_id: '' }));
  }, [formData.campaign_id]);

  // Fetch lead fields on component mount (not dependent on campaign)
  useEffect(() => {
    fetchLeadFields();
  }, []);

  // Debounce field search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFieldSearchQuery(fieldSearchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [fieldSearchQuery]);

  // Clear validation errors when inputs change
  useEffect(() => {
    setValueError('');
  }, [selectedModel, selectedField, selectedOperator, filterValue, dateInputMode]);

  const fetchInitialData = async () => {
    try {
      const [accountsResponse] = await Promise.all([
        api.get('/crm/accounts/')
      ]);
      
      // Always set an array
      const accountsData = Array.isArray(accountsResponse.data)
        ? accountsResponse.data
        : Array.isArray(accountsResponse.data.results)
          ? accountsResponse.data.results
          : Array.isArray(accountsResponse.data.accounts)
            ? accountsResponse.data.accounts
            : [];
      
      setAccounts(accountsData);
      
      // Set default account if available
      if (selectedAccount && accountsData.find(acc => acc.id === selectedAccount)) {
        setFormData(prev => ({ ...prev, account_id: selectedAccount }));
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      setAccounts([]); // fallback to empty array
    }
  };

  const fetchCampaigns = async (accountId) => {
    try {
      const response = await api.get(`/crm/campaigns/?account=${accountId}`);
      // Ensure we're setting an array, handle different response structures
      const campaignsData = Array.isArray(response.data) ? response.data : 
                           Array.isArray(response.data.results) ? response.data.results : 
                           Array.isArray(response.data.campaigns) ? response.data.campaigns : [];
      setCampaigns(campaignsData);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]); // Set empty array on error
    }
  };

  const fetchFunnels = async (campaignId) => {
    try {
      const response = await api.get(`/crm/funnels/?campaign=${campaignId}`);
      setFunnels(response.data);
    } catch (error) {
      console.error('Error fetching funnels:', error);
    }
  };

  const fetchLeadFields = async () => {
    try {
      const response = await api.get('/acs/nurturing-campaigns/lead_field_options/');
      setLeadFields(response.data);
      setFieldOptions(response.data); // Also set fieldOptions for the new filter interface
    } catch (error) {
      console.error('Error fetching lead fields:', error);
      setLeadFields([]); // Set empty array on error
      setFieldOptions([]); // Set empty array on error
    }
  };

  // Helper function to determine field group - must be defined before useMemo
  const getFieldGroup = (field) => {
    // Determine the group based on field properties
    if (field.model === 'lead_field_value') {
      return 'Custom Fields';
    }
    if (field.model === 'lead_intake_value') {
      return 'Intake Fields';
    }
    if (field.field.includes('__')) {
      return 'Related Fields';
    }
    
    // Group basic fields
    const basicFields = ['first_name', 'last_name', 'email', 'phone_number', 'status', 'type', 'source'];
    const contactFields = ['email', 'phone_number', 'address', 'city', 'state', 'country', 'zip_code'];
    const statusFields = ['status', 'type', 'source', 'stage', 'priority'];
    
    if (basicFields.includes(field.field)) {
      return 'Basic Information';
    }
    if (contactFields.includes(field.field)) {
      return 'Contact Information';
    }
    if (statusFields.includes(field.field)) {
      return 'Status & Classification';
    }
    
    return 'Other Fields';
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear errors for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'List name is required';
    }
    
    if (!formData.account_id) {
      newErrors.account_id = 'Account is required';
    }
    
    if (formData.list_type === 'dynamic' || formData.list_type === 'filtered') {
      if (filters.length === 0) {
        newErrors.filter_criteria = 'At least one filter criteria is required for dynamic/filtered lists';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (isDraft = false) => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        status: isDraft ? 'draft' : 'active',
        account: formData.account_id || null,
        account_id: formData.account_id || null,
        campaign_id: formData.campaign_id || null,
        funnel_id: formData.funnel_id || null,
        max_leads: formData.max_leads ? parseInt(formData.max_leads, 10) : null,
        filter_criteria: formData.list_type === 'dynamic' || formData.list_type === 'filtered' 
          ? filters
          : []
      };
      
      const response = await api.post('/crm/lists/', payload);
      navigate(`/crm/lists/${response.data.id}`);
    } catch (error) {
      console.error('Error creating list:', error);
      if (error.response?.data) {
        setErrors(error.response.data);
      }
    } finally {
      setSaving(false);
    }
  };



  const isDateField = (fieldName) => {
    const field = leadFields.find(f => f.field === fieldName);
    if (!field) return false;
    
    const dateTypes = ['DateField', 'DateTimeField', 'date', 'datetime'];
    const dateFieldNames = ['created_at', 'updated_at', 'last_contact_date', 'date_of_birth', 
                           'entered_at', 'exited_at', 'last_event_at', 'entered_campaign_at',
                           'exited_campaign_at', 'last_message_sent_at', 'next_scheduled_message'];
    
    return dateTypes.includes(field.type) || 
           dateFieldNames.includes(field.field) ||
           field.field.toLowerCase().includes('date') ||
           field.field.toLowerCase().includes('time') ||
           field.field.toLowerCase().includes('at');
  };

  // Helper function to check if an operator is list-based
  const isListOperator = (operator) => {
    return ['in', 'not_in'].includes(operator);
  };

  // Helper function to validate list values
  const isValidListValue = (value) => {
    if (!value || typeof value !== 'string') return false;
    const trimmedValue = value.trim();
    if (trimmedValue === '') return false;
    
    // Split by comma and check each value
    const values = trimmedValue.split(',').map(v => v.trim()).filter(v => v !== '');
    return values.length > 0;
  };

  // Helper function to validate numeric list values
  const isValidNumericList = (value) => {
    if (!isValidListValue(value)) return false;
    const values = value.split(',').map(v => v.trim()).filter(v => v !== '');
    return values.every(v => !isNaN(Number(v)));
  };

  // Helper function to validate single numeric value
  const isValidNumericValue = (value) => {
    return !isNaN(Number(value)) && value !== '';
  };

  // Helper function to check if a field is numeric
  const isNumericField = (fieldOption) => {
    if (!fieldOption) return false;
    
    const numericTypes = [
      'IntegerField', 'PositiveIntegerField', 'SmallIntegerField', 
      'PositiveSmallIntegerField', 'BigIntegerField', 'FloatField', 
      'DecimalField', 'number', 'integer', 'float', 'decimal'
    ];
    
    return numericTypes.includes(fieldOption.type);
  };

  const getOperatorsForField = (field) => {
    const fieldOption = fieldOptions.find(opt => 
      opt.model === selectedModel && opt.field === field
    );
    return fieldOption ? fieldOption.operators : [];
  };

  // Memoized field options to prevent unnecessary recalculations
  const filteredFieldOptions = useMemo(() => {
    const fields = fieldOptions
      .filter(opt => opt.model === selectedModel)
      .map(opt => ({
        value: opt.field,
        label: opt.label,
        type: opt.type,
        group: getFieldGroup(opt),
        related_model: opt.related_model,
        choices: opt.choices
      }));

    // Group fields by their group property
    const groupedFields = fields.reduce((acc, field) => {
      if (!acc[field.group]) {
        acc[field.group] = [];
      }
      acc[field.group].push(field);
      return acc;
    }, {});

    // Sort groups in a specific order
    const groupOrder = {
      'Basic Information': 0,
      'Contact Information': 1,
      'Status & Classification': 2,
      'Custom Fields': 3,
      'Intake Fields': 4,
      'Related Fields': 5,
      'Other Fields': 6
    };

    // Sort groups and their fields
    const sortedGroups = Object.entries(groupedFields)
      .sort(([groupA], [groupB]) => {
        const orderA = groupOrder[groupA] ?? 999;
        const orderB = groupOrder[groupB] ?? 999;
        return orderA - orderB;
      })
      .reduce((acc, [group, fields]) => {
        // Sort fields within each group
        const sortedFields = fields.sort((a, b) => {
          // Put fields with choices first
          if (a.choices && !b.choices) return -1;
          if (!a.choices && b.choices) return 1;
          return a.label.localeCompare(b.label);
        });
        acc[group] = sortedFields;
        return acc;
      }, {});

    // Filter fields based on debounced search query
    const filteredGroups = Object.entries(sortedGroups).reduce((acc, [group, fields]) => {
      const filteredFields = fields.filter(field => 
        field.label.toLowerCase().includes(debouncedFieldSearchQuery.toLowerCase())
      );
      if (filteredFields.length > 0) {
        acc[group] = filteredFields;
      }
      return acc;
    }, {});

    return filteredGroups;
  }, [fieldOptions, selectedModel, debouncedFieldSearchQuery]);

  const getFieldOptionsForModel = () => {
    return filteredFieldOptions;
  };

  // Helper function to detect if a field is a date field (for fieldOption)
  const isDateFieldForOption = (fieldOption) => {
    if (!fieldOption) return false;
    
    const dateTypes = ['DateField', 'DateTimeField', 'date', 'datetime'];
    const dateFieldNames = ['created_at', 'updated_at', 'last_contact_date', 'date_of_birth', 
                           'entered_at', 'exited_at', 'last_event_at', 'entered_campaign_at',
                           'exited_campaign_at', 'last_message_sent_at', 'next_scheduled_message'];
    
    return dateTypes.includes(fieldOption.type) || 
           dateFieldNames.includes(fieldOption.field) ||
           fieldOption.field.toLowerCase().includes('date') ||
           fieldOption.field.toLowerCase().includes('time') ||
           fieldOption.field.toLowerCase().includes('at');
  };

  // Helper function to get helpful examples based on field and operator
  const getHelpfulExamples = (fieldOption, operator) => {
    if (!isDateFieldForOption(fieldOption)) return [];
    
    const examples = [];
    
    // Add examples based on common use cases
    if (fieldOption.field === 'last_contact_date') {
      if (operator === 'less_than') {
        examples.push('120 days ago', '3 months ago', '1 year ago');
      } else if (operator === 'greater_than') {
        examples.push('last week', 'yesterday', '7 days ago');
      }
    } else if (fieldOption.field === 'created_at') {
      if (operator === 'greater_than') {
        examples.push('30 days ago', 'last week', 'this month');
      } else if (operator === 'less_than') {
        examples.push('1 year ago', '6 months ago');
      }
    } else if (fieldOption.field === 'date_of_birth') {
      if (operator === 'less_than') {
        examples.push('18 years ago', '21 years ago');
      } else if (operator === 'greater_than') {
        examples.push('65 years ago', '50 years ago');
      }
    }
    
    // Add general examples if no specific ones
    if (examples.length === 0) {
      examples.push('120 days ago', 'last week', 'this month', 'yesterday');
    }
    
    return examples;
  };

  // Helper function to validate relative date expressions
  const isValidRelativeDate = (value) => {
    if (!value || typeof value !== 'string') return false;
    
    const trimmedValue = value.trim().toLowerCase();
    
    // Check for common relative date patterns
    const patterns = [
      /^\d+\s+(day|days|week|weeks|month|months|year|years)\s+ago$/,
      /^\d+\s+(day|days|week|weeks|month|months|year|years)\s+from\s+now$/,
      /^(today|yesterday|tomorrow)$/,
      /^(last|next|this)\s+(week|month|year)$/
    ];
    
    return patterns.some(pattern => pattern.test(trimmedValue));
  };

  // Helper function to validate absolute date format
  const isValidAbsoluteDate = (value) => {
    if (!value || typeof value !== 'string') return false;
    
    // Check for datetime-local format (YYYY-MM-DDTHH:MM)
    const datetimePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
    if (datetimePattern.test(value)) {
      const date = new Date(value);
      return !isNaN(date.getTime());
    }
    
    // Check for YYYY-MM-DD format
    const datePattern = /^\d{4}-\d{2}-\d{2}$/;
    if (!datePattern.test(value)) return false;
    
    const date = new Date(value);
    return !isNaN(date.getTime());
  };

  // Helper function to validate date input (both relative and absolute)
  const isValidDateInput = (value) => {
    if (!value) return true; // Empty is valid (will be handled by required validation)
    
    // If we're in absolute mode, validate as absolute date
    if (dateInputMode === 'absolute') {
      return isValidAbsoluteDate(value);
    }
    
    // If we're in relative mode, no validation needed since it's a dropdown with predefined values
    if (dateInputMode === 'relative') {
      return true; // All dropdown options are valid
    }
    
    // For list operators, check if it's a valid list of dates
    if (isListOperator(selectedOperator)) {
      const values = value.split(',').map(v => v.trim()).filter(v => v !== '');
      return values.every(v => isValidRelativeDate(v) || isValidAbsoluteDate(v));
    }
    
    // Check if it's a relative date expression first
    if (isValidRelativeDate(value)) {
      return true;
    }
    
    // If not a relative date, validate as absolute date
    return isValidAbsoluteDate(value);
  };

  const formatOperatorLabel = (operator) => {
    const operatorMap = {
      'equals': 'Equals',
      'contains': 'Contains',
      'does_not_contain': 'Does Not Contain',
      'starts_with': 'Starts With',
      'does_not_start_with': 'Does Not Start With',
      'ends_with': 'Ends With',
      'does_not_end_with': 'Does Not End With',
      'in': 'In',
      'not_in': 'Not In',
      'greater_than': 'Greater Than',
      'less_than': 'Less Than',
      'greater_than_or_equal': 'Greater Than or Equal',
      'less_than_or_equal': 'Less Than or Equal',
      'is_empty': 'Is Empty',
      'is_not_empty': 'Is Not Empty'
    };
    return operatorMap[operator] || operator;
  };

  const formatFilterLabel = (filter) => {
    const fieldOption = fieldOptions.find(opt => 
      opt.model === filter.model && opt.field === filter.field
    );
    
    if (!fieldOption) return `${filter.field} ${filter.operator} ${filter.value}`;

    const fieldLabel = fieldOption.label;
    const operatorLabel = formatOperatorLabel(filter.operator);
    
    if (filter.operator === 'is_empty' || filter.operator === 'is_not_empty') {
      return `${fieldLabel} ${operatorLabel}`;
    }
    
    // Special formatting for relative date expressions
    const isRelativeDate = isDateFieldForOption(fieldOption) && 
      typeof filter.value === 'string' && 
      (filter.value.includes('ago') || 
       filter.value.includes('next ') ||
       filter.value.includes('this '));
    
    if (isRelativeDate) {
      return `${fieldLabel} ${operatorLabel} "${filter.value}"`;
    }

    // Special formatting for list-based operators
    if (isListOperator(filter.operator)) {
      const values = filter.value.split(',').map(v => v.trim()).filter(v => v !== '');
      if (values.length === 1) {
        return `${fieldLabel} ${operatorLabel} "${values[0]}"`;
      } else {
        return `${fieldLabel} ${operatorLabel} [${values.map(v => `"${v}"`).join(', ')}]`;
      }
    }
    
    return `${fieldLabel} ${operatorLabel} ${filter.value}`;
  };

  // Filter handling functions (matching LeadSelectionInterface)
  const handleAddFilter = () => {
    if (!selectedModel || !selectedField || !selectedOperator) return;

    const fieldOption = fieldOptions.find(opt => 
      opt.model === selectedModel && opt.field === selectedField
    );

    // Validate value based on operator and field type
    if (selectedOperator !== 'is_empty' && selectedOperator !== 'is_not_empty') {
      if (!filterValue) {
        setValueError('Value is required');
        return;
      }

      // Validate list-based operators
      if (isListOperator(selectedOperator)) {
        if (!isValidListValue(filterValue)) {
          setValueError('Enter comma-separated values (e.g., "value1, value2, value3")');
          return;
        }
      }

      // Validate date fields
      if (isDateFieldForOption(fieldOption)) {
        if (!isValidDateInput(filterValue)) {
          setValueError('Invalid date format. Use YYYY-MM-DD format or relative expressions like "120 days ago"');
          return;
        }
      }

      // Validate numeric fields
      if (isNumericField(fieldOption)) {
        if (isListOperator(selectedOperator)) {
          if (!isValidNumericList(filterValue)) {
            setValueError('Enter comma-separated numbers (e.g., "10, 20, 30")');
            return;
          }
        } else {
          if (!isValidNumericValue(filterValue)) {
            setValueError('Enter a valid number');
            return;
          }
        }
      }
    }

    const newFilter = {
      model: selectedModel,
      field: selectedField,
      operator: selectedOperator,
      value: filterValue,
      combine_with: combineWith
    };

    setFilters(prev => [...prev, newFilter]);
    setSelectedModel('');
    setSelectedField('');
    setSelectedOperator('');
    setFilterValue('');
    setDateInputMode('absolute');
    setValueError('');
  };

  const handleRemoveFilter = (index) => {
    setFilters(prev => prev.filter((_, i) => i !== index));
  };

  // Helper function to get placeholder text for string operators
  const getStringOperatorPlaceholder = (operator) => {
    switch (operator) {
      case 'contains':
        return 'Enter text to search for...';
      case 'does_not_contain':
        return 'Enter text to exclude...';
      case 'starts_with':
        return 'Enter text that the field should start with...';
      case 'does_not_start_with':
        return 'Enter text that the field should not start with...';
      case 'ends_with':
        return 'Enter text that the field should end with...';
      case 'does_not_end_with':
        return 'Enter text that the field should not end with...';
      default:
        return 'Enter value...';
    }
  };

  const renderOperatorInput = () => {
    if (!selectedOperator) return null;

    const fieldOption = fieldOptions.find(opt => 
      opt.model === selectedModel && opt.field === selectedField
    );

    if (!fieldOption) return null;

    if (selectedOperator === 'is_empty' || selectedOperator === 'is_not_empty') {
      return null;
    }

    // Handle fields with predefined choices
    if (fieldOption.choices && fieldOption.choices.length > 0) {
      return (
        <FormControl fullWidth>
          <InputLabel>Value</InputLabel>
          <Select
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            label="Value"
          >
            {fieldOption.choices.map(choice => (
              <MenuItem key={choice.value} value={choice.value}>
                {choice.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    // Handle date fields with relative date support
    if (isDateFieldForOption(fieldOption)) {
      const helpfulExamples = getHelpfulExamples(fieldOption, selectedOperator);
      
      return (
        <Box>
          {/* Absolute Date Input */}
          {dateInputMode === 'absolute' && (
            <TextField
              type="datetime-local"
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              fullWidth
              placeholder="Select date and time"
              InputLabelProps={{ shrink: true }}
              error={!!valueError}
              helperText={valueError}
            />
          )}

          {/* Relative Date Input */}
          {dateInputMode === 'relative' && (
            <Box>
              <FormControl fullWidth error={!!valueError}>
                <InputLabel>Relative Date Expression</InputLabel>
                <Select
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  label="Relative Date Expression"
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 400
                      }
                    }
                  }}
                >
                  {/* Time Ago Expressions */}
                  <ListSubheader>
                    <Typography variant="subtitle2" color="primary">
                      Time Ago
                    </Typography>
                  </ListSubheader>
                  {['1 day ago', '2 days ago', '3 days ago', '7 days ago', '14 days ago', '30 days ago', '60 days ago', '90 days ago', '120 days ago', '180 days ago', '1 year ago', '2 years ago'].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                  
                  {/* Weeks Ago */}
                  <ListSubheader>
                    <Typography variant="subtitle2" color="primary">
                      Weeks Ago
                    </Typography>
                  </ListSubheader>
                  {['1 week ago', '2 weeks ago', '3 weeks ago', '4 weeks ago', '8 weeks ago', '12 weeks ago'].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                  
                  {/* Months Ago */}
                  <ListSubheader>
                    <Typography variant="subtitle2" color="primary">
                      Months Ago
                    </Typography>
                  </ListSubheader>
                  {['1 month ago', '2 months ago', '3 months ago', '6 months ago', '9 months ago', '12 months ago'].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                  
                  {/* Common Expressions */}
                  <ListSubheader>
                    <Typography variant="subtitle2" color="primary">
                      Common Expressions
                    </Typography>
                  </ListSubheader>
                  {['today', 'yesterday', 'tomorrow'].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                  
                  {/* Period-Based Expressions */}
                  <ListSubheader>
                    <Typography variant="subtitle2" color="primary">
                      Period-Based
                    </Typography>
                  </ListSubheader>
                  {['this week', 'last week', 'next week', 'this month', 'last month', 'next month', 'this year', 'last year', 'next year'].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                  
                  {/* Time From Now */}
                  <ListSubheader>
                    <Typography variant="subtitle2" color="primary">
                      Time From Now
                    </Typography>
                  </ListSubheader>
                  {['1 day from now', '2 days from now', '7 days from now', '14 days from now', '30 days from now', '1 week from now', '2 weeks from now', '1 month from now', '3 months from now', '6 months from now', '1 year from now'].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {valueError && <FormHelperText>{valueError}</FormHelperText>}
              </FormControl>
              
              {/* Helpful Examples Section */}
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  onClick={() => setShowRelativeDateHelp(!showRelativeDateHelp)}
                  sx={{ minWidth: 'auto', p: 0 }}
                >
                  {showRelativeDateHelp ? 'Hide' : 'Show'} helpful examples for {fieldOption.label}
                </Button>
                {showRelativeDateHelp && (
                  <Paper variant="outlined" sx={{ mt: 1, p: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Helpful Examples for {fieldOption.label}:</strong>
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {helpfulExamples.map((example, index) => (
                        <Chip
                          key={index}
                          label={example}
                          size="small"
                          variant="outlined"
                          onClick={() => setFilterValue(example)}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                  </Paper>
                )}
              </Box>
            </Box>
          )}
        </Box>
      );
    }

    // Handle list-based operators
    if (isListOperator(selectedOperator)) {
      const placeholder = isDateFieldForOption(fieldOption) 
        ? 'Enter dates separated by commas (e.g., "2024-01-01, 2024-01-15, 30 days ago")'
        : isNumericField(fieldOption)
        ? 'Enter numbers separated by commas (e.g., "10, 20, 30")'
        : 'Enter values separated by commas (e.g., "value1, value2, value3")';
      
      return (
        <TextField
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value)}
          fullWidth
          placeholder={placeholder}
          error={!!valueError}
          helperText={valueError}
        />
      );
    }

    switch (fieldOption.type) {
      case 'IntegerField':
      case 'PositiveIntegerField':
      case 'SmallIntegerField':
      case 'PositiveSmallIntegerField':
      case 'BigIntegerField':
      case 'FloatField':
      case 'DecimalField':
      case 'number':
        return (
          <TextField
            type="number"
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            fullWidth
            inputProps={{ step: fieldOption.type === 'IntegerField' ? '1' : 'any' }}
            error={!!valueError}
            helperText={valueError}
          />
        );
      case 'CharField':
      case 'TextField':
      case 'EmailField':
      case 'URLField':
      case 'text':
      case 'string':
        return (
          <TextField
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            fullWidth
            placeholder={getStringOperatorPlaceholder(selectedOperator)}
            error={!!valueError}
            helperText={valueError}
          />
        );
      default:
        return (
          <TextField
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            fullWidth
            error={!!valueError}
            helperText={valueError}
          />
        );
    }
  };



  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link color="inherit" href="/crm/dashboard">
            Dashboard
          </Link>
          <Link color="inherit" href="/crm/lists">
            Lead Lists
          </Link>
          <Typography color="text.primary">Create List</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1" color="text.primary">
            Create Lead List
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => navigate('/crm/lists')}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleSave(true)}
              disabled={saving}
              sx={{ mr: 1 }}
            >
              Save Draft
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => handleSave(false)}
              disabled={saving}
            >
              Create List
            </Button>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Basic Information
              </Typography>
              
              <TextField
                fullWidth
                label="List Name *"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
              
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <FormLabel component="legend">List Type *</FormLabel>
                <RadioGroup
                  value={formData.list_type}
                  onChange={(e) => handleInputChange('list_type', e.target.value)}
                >
                  <FormControlLabel value="manual" control={<Radio />} label="Manual" />
                  <FormControlLabel value="dynamic" control={<Radio />} label="Dynamic" />
                  <FormControlLabel value="filtered" control={<Radio />} label="Filtered" />
                </RadioGroup>
              </FormControl>
              
              <FormControl component="fieldset">
                <FormLabel component="legend">Status *</FormLabel>
                <RadioGroup
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <FormControlLabel value="active" control={<Radio />} label="Active" />
                  <FormControlLabel value="draft" control={<Radio />} label="Draft" />
                </RadioGroup>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        {/* Organizational Context */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Organizational Context
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.account_id}>
                <InputLabel>Account *</InputLabel>
                <Select
                  value={formData.account_id}
                  onChange={(e) => handleInputChange('account_id', e.target.value)}
                  label="Account *"
                >
                  {(Array.isArray(accounts) ? accounts : []).map(account => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.account_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.account_id}
                  </Typography>
                )}
              </FormControl>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Campaign</InputLabel>
                <Select
                  value={formData.campaign_id}
                  onChange={(e) => handleInputChange('campaign_id', e.target.value)}
                  label="Campaign"
                  disabled={!formData.account_id}
                >
                  <MenuItem value="">No Campaign</MenuItem>
                  {(Array.isArray(campaigns) ? campaigns : []).map(campaign => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl fullWidth>
                <InputLabel>Funnel</InputLabel>
                <Select
                  value={formData.funnel_id}
                  onChange={(e) => handleInputChange('funnel_id', e.target.value)}
                  label="Funnel"
                  disabled={!formData.campaign_id}
                >
                  <MenuItem value="">No Funnel</MenuItem>
                  {(Array.isArray(funnels) ? funnels : []).map(funnel => (
                    <MenuItem key={funnel.id} value={funnel.id}>
                      {funnel.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        {/* List Properties */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                List Properties
              </Typography>
              
              <FormGroup sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_public}
                      onChange={(e) => handleInputChange('is_public', e.target.checked)}
                    />
                  }
                  label="Public (visible to all account members)"
                />
              </FormGroup>
              
              <TextField
                fullWidth
                label="Maximum Leads (optional)"
                type="number"
                value={formData.max_leads}
                onChange={(e) => handleInputChange('max_leads', e.target.value)}
                helperText="Leave empty for unlimited leads"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Dynamic Filter Builder */}
        {(formData.list_type === 'dynamic' || formData.list_type === 'filtered') && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                    Lead Selection
                  </Typography>
                  <Tooltip title="Filter leads by contact info, company details, deal status, and more. Date fields support dynamic expressions like '120 days ago' or 'last week'">
                    <IconButton size="small" sx={{ ml: 1 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                
                {errors.filter_criteria && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.filter_criteria}
                  </Alert>
                )}



                <Paper sx={{ p: 2, mb: 2 }}>
                  {/* First Row: Model and Field Selection */}
                  <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Model</InputLabel>
                        <Select
                          value={selectedModel}
                          onChange={(e) => {
                            setSelectedModel(e.target.value);
                            setSelectedField('');
                            setSelectedOperator('');
                            setFilterValue('');
                            setDateInputMode('absolute');
                            setValueError('');
                          }}
                          label="Model"
                        >
                          {Array.from(new Set(fieldOptions.map(opt => opt.model))).map(model => (
                            <MenuItem key={model} value={model}>
                              {model.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Field</InputLabel>
                        <Select
                          key={`field-select-${selectedModel}`}
                          value={selectedField}
                          onChange={(e) => {
                            setSelectedField(e.target.value);
                            setSelectedOperator('');
                            setFilterValue('');
                            setDateInputMode('absolute');
                            setValueError('');
                          }}
                          label="Field"
                          disabled={!selectedModel}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300
                              }
                            }
                          }}
                        >
                          <ListSubheader>
                            <TextField
                              ref={fieldSearchInputRef}
                              size="small"
                              autoFocus
                              placeholder="Search fields..."
                              fullWidth
                              value={fieldSearchQuery}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon />
                                  </InputAdornment>
                                )
                              }}
                              onChange={(e) => setFieldSearchQuery(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key !== 'Escape') {
                                  e.stopPropagation();
                                }
                              }}
                              onFocus={(e) => {
                                // Prevent the select from closing when clicking on the search input
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                // Prevent the select from closing when clicking on the search input
                                e.stopPropagation();
                              }}
                            />
                          </ListSubheader>
                          {Object.entries(getFieldOptionsForModel()).map(([group, fields]) => [
                            <ListSubheader key={group}>{group}</ListSubheader>,
                            ...fields.map(field => (
                              <MenuItem key={field.value} value={field.value}>
                                {field.label}
                              </MenuItem>
                            ))
                          ])}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {/* Second Row: Date Mode Selection (only for date fields) */}
                  {selectedField && isDateFieldForOption(fieldOptions.find(opt => opt.model === selectedModel && opt.field === selectedField)) && (
                    <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mr: 1 }}>
                            Choose Date Input Type:
                          </Typography>
                          <Chip 
                            label="Date Field" 
                            size="small" 
                            color="info" 
                            variant="outlined"
                            icon={<span>📅</span>}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant={dateInputMode === 'absolute' ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => {
                              setDateInputMode('absolute');
                              setFilterValue('');
                              setValueError('');
                            }}
                            startIcon={<span>📅</span>}
                            sx={{ 
                              minWidth: '140px',
                              justifyContent: 'flex-start'
                            }}
                          >
                            Absolute Date/Time
                          </Button>
                          <Button
                            variant={dateInputMode === 'relative' ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => {
                              setDateInputMode('relative');
                              setFilterValue('');
                              setValueError('');
                            }}
                            startIcon={<span>🔄</span>}
                            sx={{ 
                              minWidth: '140px',
                              justifyContent: 'flex-start'
                            }}
                          >
                            Relative Date
                          </Button>
                        </Box>
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                          {dateInputMode === 'absolute' 
                            ? 'Select a specific date and time (e.g., "2024-01-15 14:30")'
                            : 'Choose from predefined relative date expressions (e.g., "120 days ago", "last week")'
                          }
                        </Typography>
                      </Grid>
                    </Grid>
                  )}

                  {/* Third Row: Operator and Value Selection */}
                  {selectedField && (
                    <Grid container spacing={2} alignItems="flex-start">
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Operator</InputLabel>
                          <Select
                            value={selectedOperator}
                            onChange={(e) => {
                              setSelectedOperator(e.target.value);
                              setFilterValue('');
                              setDateInputMode('absolute');
                              setValueError('');
                            }}
                            label="Operator"
                          >
                            {getOperatorsForField(selectedField).map(operator => (
                              <MenuItem key={operator.value} value={operator.value}>
                                {operator.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} md={8}>
                        {renderOperatorInput()}
                      </Grid>
                      
                      <Grid item xs={12} md={1}>
                        <Button
                          variant="contained"
                          onClick={handleAddFilter}
                          disabled={
                            !selectedModel || 
                            !selectedField || 
                            !selectedOperator || 
                            ((selectedOperator !== 'is_empty' && selectedOperator !== 'is_not_empty') && 
                             (!filterValue || !!valueError))
                          }
                          fullWidth
                          sx={{ height: '56px' }} // Match the height of other inputs
                        >
                          <AddIcon />
                        </Button>
                      </Grid>
                    </Grid>
                  )}

                  {/* Combine Conditions Row (only when filters exist) */}
                  {filters.length > 0 && (
                    <Grid container spacing={2} sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>Combine Conditions With</InputLabel>
                          <Select
                            value={combineWith}
                            onChange={(e) => setCombineWith(e.target.value)}
                            label="Combine Conditions With"
                          >
                            <MenuItem value="AND">AND (All conditions must match)</MenuItem>
                            <MenuItem value="OR">OR (Any condition can match)</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  )}
                </Paper>

                {filters.length > 0 && (
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Active Filters
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {filters.map((filter, index) => (
                        <Chip
                          key={index}
                          label={formatFilterLabel(filter)}
                          onDelete={() => handleRemoveFilter(index)}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Paper>
                )}

                {filters.length === 0 && (
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Getting Started with Lead Selection
                    </Typography>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      Use the filters above to select leads for your list. Date fields support both absolute dates (YYYY-MM-DD) and relative expressions for dynamic filtering.
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip label="Example: '120 days ago'" size="small" variant="outlined" />
                      <Chip label="Example: 'last week'" size="small" variant="outlined" />
                      <Chip label="Example: 'this month'" size="small" variant="outlined" />
                      <Chip label="Example: '30 days from now'" size="small" variant="outlined" />
                    </Box>
                  </Paper>
                )}


              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default CreateLeadList; 